<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, computed } from 'vue'

import RoomieNavigationDots from '@lahaus-roomie/roomie/src/components/RoomieNavigationDots/index.vue'
import RoomieNavigationButton from '@lahaus-roomie/roomie/src/components/RoomieNavigationButton/index.vue'
import RoomieButton from '@lahaus-roomie/roomie/src/components/RoomieButton/index.vue'
import IconChevronLeft from '@lahaus-roomie/roomie/src/assets/icons/chevron-left.svg'
import IconChevronRight from '@lahaus-roomie/roomie/src/assets/icons/chevron-right.svg'
import BackIcon from '@lahaus-roomie/roomie/src/assets/icons/v2/back.svg'
import NextIcon from '@lahaus-roomie/roomie/src/assets/icons/v2/next.svg'
import IconImage from '@lahaus-roomie/roomie/src/assets/icons/image.svg'

import PhotoSwipeLightbox from 'photoswipe/lightbox'
import Photoswipe from 'photoswipe'
import 'photoswipe/style.css'

import type { Props, Source } from './types'
import { useThemeStore } from '@/stores/useThemeStore'

const { isV2 } = useThemeStore()

const { t } = useI18n()

const props = withDefaults(defineProps<Props>(), {
  imageMultiplier: 5,
  itemsPerSlide: 1,
  showMobileArrows: false
})
const emit = defineEmits(['imageClicked', 'imageScrolled'])

const picturesTouched = ref(false)
const sliderGallery = ref()

const picturesSize = ref([{ width: 0, height: 0 }])

const indexImage = ref(0)

const lightbox = ref()

const scrollOffset = ref(0)

const resolvePicturesLoad = () => {
  if (!props.zoom && props.smallLoad) {
    return props.picturesSources.slice(0, 2 * props.itemsPerSlide + 1)
  }

  return props.picturesSources
}

const pictures = ref(resolvePicturesLoad())

const scrollEvent = () => {
  if (!picturesTouched.value) {
    pictures.value = props.picturesSources
    picturesTouched.value = true
  }

  if (sliderGallery.value) {
    scrollOffset.value = sliderGallery.value.scrollLeft
    const imageWidth = sliderGallery.value.offsetWidth / props.itemsPerSlide

    indexImage.value = Math.round(scrollOffset.value / imageWidth)
    emit('imageScrolled', indexImage.value)
  }
}

const changeImage = (direction: string, reset?: boolean) => {
  if (!picturesTouched.value && !reset) {
    pictures.value = props.picturesSources
    picturesTouched.value = true
  }

  if (sliderGallery.value?.scrollBy) {
    const imageWidth = sliderGallery.value.offsetWidth / props.itemsPerSlide
    const scrollDistance = direction === 'left' ? -imageWidth : imageWidth

    if (reset) {
      indexImage.value = 0
      picturesTouched.value = false
    }

    sliderGallery.value.scrollBy({
      left: reset ? 0 : scrollDistance,
      behavior: 'smooth'
    })
  }
}

const getPictureSize = () => {
  const elements = document.getElementsByClassName('carrousel-picture-class')

  if (elements) {
    picturesSize.value = []

    for (const element of elements) {
      // @ts-ignore
      const width = element.offsetWidth * props.imageMultiplier
      // @ts-ignore
      const height = element.offsetHeight * props.imageMultiplier

      if (props.sizeReverse) {
        picturesSize.value.splice(0, 0, { width, height })
      } else {
        picturesSize.value.push({ width, height })
      }
    }
  }
}

const handleImageClick = (index: number) => {
  if (props.zoom) getPictureSize()
  emit('imageClicked', index)
}

const resetPosition = () => {
  if (sliderGallery.value?.scrollTo) {
    sliderGallery.value.scrollTo(0, 0)
  }
  changeImage('left', true)
}

const initLightbox = () => {
  if (props.zoom) {
    lightbox.value = new PhotoSwipeLightbox({
      gallerySelector: '#app-carrousel-picture-container',
      childSelector: 'a',
      initialZoomLevel: 'fit',
      secondaryZoomLevel: 1.4,
      maxZoomLevel: 3,
      hideAnimationDuration: 0,
      pswpModule: Photoswipe,
      wheelToZoom: true,
      preload: [1, 2]
    })

    lightbox.value.init()
  }
}

const destroyLightbox = () => {
  if (lightbox.value) {
    lightbox.value.destroy()
    lightbox.value = null
  }
}

const getHref = (pictureSource: Source[]) => pictureSource[pictureSource.length - 1].src

const getFormatImage = (picture: Source) => {
  const pictureUrl = new URLSearchParams(picture.src as string)

  if (picture?.imageWidth) pictureUrl.append('w', picture.imageWidth.toString())
  if (picture?.imageHeight) pictureUrl.append('h', picture.imageHeight.toString())

  return pictureUrl?.toString()
}

const resolveScreenHeight = computed(() => window?.innerHeight <= 650)

watch(() => props.picturesSources, () => {
  resetPosition()
})

onMounted(() => {
  initLightbox()
  resetPosition()
})

onUnmounted(() => {
  destroyLightbox()
})

const width = computed(() => (1 / props.itemsPerSlide) * 100)

const numberOfNavDots = props.picturesSources.length - props.itemsPerSlide + 1

</script>

<script lang="ts">
export default {
  name: 'AppCarrousel'
}
</script>

<template>
  <div
    :id="id"
    class="app-carrousel scrollbar-hidden flex overflow-auto relative">
    <RoomieNavigationButton
      v-if="picturesSources?.length > 0"
      :id="`app-carrousel-left-button-${id}`"
      :data-lh-id="`app-carrousel-left-button-${id}`"
      variant="outlined"
      size="lg"
      class="absolute z-10 -translate-y-1/2 top-1/2 left-4"
      :class="{
        'opacity-0 cursor-default': indexImage === 0,
        'top-[70%]': resolveScreenHeight,
        'hidden lg:block': !showMobileArrows
      }"
      aria-label="ir a la imagen anterior"
      @click="changeImage('left')">
      <BackIcon
        v-if="isV2" />

      <IconChevronLeft
        v-else
        class="fill-primary-600 pointer-events-none" />
    </RoomieNavigationButton>

    <div
      id="app-carrousel-picture-container"
      ref="sliderGallery"
      :data-lh-id="`app-carrousel-picture-container-slider-${id}`"
      class="app-carrousel-picture-container scrollbar-hidden flex items-center overflow-auto"
      :class="pictureContainerStyles"
      @scroll="scrollEvent">
      <figure
        v-for="(pictureSource, index) in pictures"
        :key="index"
        class="w-full h-full flex items-center relative flex-[0_0_auto]"
        :class="{ 'app-carrousel--opacity': showSeeMoreCta && index === picturesSources.length - 1}"
        :style="[figureStyles, { width: `${width}%` }]">
        <component
          :is="zoom ? 'a' : 'div'"
          :id="`app-carrousel-picture-link-${index}`"
          class="app-carrousel-picture-link"
          :class="anchorStyles"
          :data-lh-id="`app-carrousel-picture-link-${id}`"
          :href="getHref(pictureSource)"
          :data-pswp-width="picturesSize?.[index]?.width"
          :data-pswp-height="picturesSize?.[index]?.height"
          @click="handleImageClick(index)">
          <AppImage
            alt="imagenes proyecto"
            :sources="pictureSource"
            class="flex w-full"
            :class-img="['carrousel-picture-class mx-auto object-contain pointer-events-none', imgStyles as string]"
            :src="getFormatImage(pictureSource[0])"
            :data-lh-id="`app-carrousel-picture-link-${id}`" />

          <RoomieButton
            v-if="showSeeMoreCta && index === picturesSources.length - 1"
            :id="`app-carrousel-button-see-more-${id}`"
            :data-lh-id="`app-carrousel-button-see-more-${id}`"
            :aria-label="t('seeMore')"
            class="!bg-white font-medium absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 whitespace-nowrap"
            :class="{ '!cursor-default': index !== picturesSources.length - 1 }"
            variant="outlined"
            icon-placement="left">
            <template #icon>
              <IconImage />
            </template>

            <span> {{ t('seeMore') }} </span>
          </RoomieButton>
        </component>
      </figure>
    </div>

    <RoomieNavigationDots
      v-if="picturesSources.length > itemsPerSlide"
      class="absolute bottom-4 left-1/2 -translate-x-1/2 pointer-events-none"
      :class="[dotsStyles]"
      transparent-background
      :number-of-images="numberOfNavDots"
      :current-position="indexImage" />

    <RoomieNavigationButton
      v-if="picturesSources.length > 1"
      :id="`app-carrousel-right-button-${id}`"
      :data-lh-id="`app-carrousel-right-button-${id}`"
      class="absolute z-10 -translate-y-1/2 top-1/2 right-4"
      aria-label="ir a la imagen siguiente"
      variant="outlined"
      size="lg"
      :class="{
        'opacity-0 cursor-default': indexImage + props.itemsPerSlide === picturesSources.length,
        'top-[70%]': resolveScreenHeight,
        'hidden lg:block': !showMobileArrows
      }"
      @click="changeImage('right')">
      <NextIcon v-if="isV2" />

      <IconChevronRight
        v-else
        class="fill-primary-600 pointer-events-none" />
    </RoomieNavigationButton>
  </div>
</template>

<style lang="scss">
.app-carrousel {
  &-picture-container {
    @apply overflow-y-hidden;

    min-width: 100%;
    scroll-snap-type: x mandatory;
    scroll-snap-stop: always;

    figure {
      scroll-snap-align: center;
    }
  }

}

.app-carrousel--opacity {
  @apply relative;

  &::before {
    @apply absolute w-full h-full bg-carbon-800 bg-opacity-50;

    content: '';
  }
}

.pswp {
  &__button {
    &--arrow--next,
    &--arrow--prev {
      @apply w-50 h-50 mx-16;
      @apply bg-white rounded-full shadow-[0_0_10px_#0000004f];
      @apply grid place-content-center;
      @apply hover:bg-white;
    }

    &--arrow--next {
      & .pswp__icn {
        @apply fill-primary-600 top-10 -left-4 scale-[.6] rotate-180;
      }
    }

    &--arrow--prev {
      & .pswp__icn {
        @apply fill-primary-600 top-10 -left-0 scale-[.6];
      }
    }
  }

  &__icn-shadow {
    @apply hidden;
  }

  &__counter {
    @apply text-18 font-medium;
  }
}
</style>

<i18n src="./i18n.json" lang="json" />
